import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationServices } from '@/modules/common/types/integrations';

/**
 * Domain model for integration status
 * Application's internal representation of an integration's connection state
 */
export enum IntegrationStatus {
  Connected = 'CONNECTED',
  Error = 'ERROR',
  Syncing = 'SYNCING',
  Unknown = 'UNKNOWN',
}

/**
 * Domain model for organization-wide integration status
 * Application's internal representation of overall integration health
 */
export enum OrganizationIntegrationStatus {
  Idle = 'IDLE',
  Syncing = 'SYNCING',
  SyncFailed = 'SYNC_FAILED',
  HasConflicts = 'HAS_CONFLICTS',
  Unknown = 'UNKNOWN',
}

/**
 * Domain model for entity-level integration status
 * Application's internal representation of entity sync state
 */
export enum IntegrationSyncingStatus {
  Syncing = 'SYNCING',
  Idle = 'IDLE',
}

export enum IntegrationDataStatus {
  Idle = 'IDLE',
  Error = 'ERROR',
  ResolvedConflictError = 'RESOLVED_CONFLICT_ERROR',
}

export enum IntegrationErrorCode {
  SYNC_FAILED = 'SYNC_FAILED',
  // Error args: [number of duplicates]
  DUPLICATES_DETECTED = 'DUPLICATES_DETECTED',
  // Error args: [number of validation errors]
  VALIDATION_ERRORS = 'VALIDATION_ERRORS',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  WEBHOOK_VERIFICATION_TIMEOUT = 'WEBHOOK_VERIFICATION_TIMEOUT',
}

export interface ConflictData {
  entityType: EntityType;
  isResolved: boolean;
}

/**
 * Domain model for integration error
 * Represents an error that occurred during integration operations
 */
export interface IntegrationError {
  occurredAt: Date;
  code: IntegrationErrorCode;
  args: Record<string, any>;
}

/**
 * Domain model for entity-level integration status descriptor
 * Contains sync status and details for a specific entity type within an integration
 */
export interface EntityIntegrationStatusDescriptor {
  syncingStatus: IntegrationSyncingStatus;
  dataStatus: IntegrationDataStatus;
  entityType: EntityType;
  lastSyncErrors?: IntegrationError[];
  lastSyncedAt?: Date;
}

/**
 * Domain model for integration status descriptor
 * Contains complete status information for a single integration provider
 */
export interface IntegrationStatusDescriptor {
  integration: IntegrationServices;
  status: IntegrationStatus;
  entities: EntityIntegrationStatusDescriptor[];
  errors?: IntegrationError[];
  lastSyncedAt?: Date;
}

/**
 * Domain model for organization integration status
 * Top-level model containing overall status and all integration statuses
 */
export interface OrganizationIntegrationStatusData {
  status: OrganizationIntegrationStatus;
  integrations: IntegrationStatusDescriptor[];
  conflicts: ConflictData[];
}
